import React, { useEffect, useMemo, useState } from 'react';
import { axiosInstance } from '../../App';
import { toast } from 'sonner';
import {
  History, X, RotateCcw, GitCompareArrows, Archive, ArchiveRestore,
  Combine, ChevronRight, ChevronLeft, Plus, Minus, Equal, FileEdit,
  Trash2, FilePlus, RefreshCcw,
} from 'lucide-react';


/**
 * RuleHistoryDrawer — version list + diff viewer + restore actions
 * for ONE bank-matching-rule. Opens as a right-side drawer.
 */


const ACTION_LABEL = {
  create:    { ar: 'إنشاء',       icon: FilePlus,   tone: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  update:    { ar: 'تَعديل',      icon: FileEdit,   tone: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
  delete:    { ar: 'حَذف',        icon: Trash2,     tone: 'bg-red-100 text-red-800 border-red-200' },
  restore:   { ar: 'استِعادَة',   icon: RotateCcw,  tone: 'bg-amber-100 text-amber-800 border-amber-200' },
  merge:     { ar: 'دَمج',        icon: Combine,    tone: 'bg-sky-100 text-sky-800 border-sky-200' },
  archive:   { ar: 'أَرشَفَة',     icon: Archive,    tone: 'bg-zinc-100 text-zinc-800 border-zinc-200' },
  unarchive: { ar: 'فَك أَرشَفَة', icon: ArchiveRestore, tone: 'bg-zinc-100 text-zinc-800 border-zinc-200' },
};


function VersionRow({ v, isSelectedA, isSelectedB, onClick, onCompareA, onCompareB, onRestore }) {
  const info = ACTION_LABEL[v.action] || ACTION_LABEL.update;
  const Icon = info.icon;
  return (
    <div className={`rounded-lg border p-2.5 transition cursor-pointer ${
      isSelectedA ? 'bg-indigo-50 border-indigo-400 ring-1 ring-indigo-300' :
      isSelectedB ? 'bg-emerald-50 border-emerald-400 ring-1 ring-emerald-300' :
                    'bg-white border-slate-200 hover:bg-slate-50'}`}
         onClick={onClick}
         data-testid={`version-${v.version_no}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs bg-slate-900 text-white rounded px-1.5 py-0.5">v{v.version_no}</span>
          <span className={`inline-flex items-center gap-1 text-[10px] font-semibold border rounded-full px-2 py-0.5 ${info.tone}`}>
            <Icon className="w-3 h-3" />{info.ar}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={(e) => { e.stopPropagation(); onCompareA(v.version_no); }}
                  data-testid={`compare-a-${v.version_no}`}
                  title="مُقارَنَة كَـ A"
                  className="text-[10px] font-mono rounded px-1.5 py-0.5 bg-indigo-100 hover:bg-indigo-200 text-indigo-800">
            A
          </button>
          <button onClick={(e) => { e.stopPropagation(); onCompareB(v.version_no); }}
                  data-testid={`compare-b-${v.version_no}`}
                  title="مُقارَنَة كَـ B"
                  className="text-[10px] font-mono rounded px-1.5 py-0.5 bg-emerald-100 hover:bg-emerald-200 text-emerald-800">
            B
          </button>
          {v.action !== 'delete' && (
            <button onClick={(e) => { e.stopPropagation(); onRestore(v.version_no); }}
                    data-testid={`restore-${v.version_no}`}
                    title="استِرجاع هَذا الإصدار"
                    className="text-[10px] font-mono rounded px-1.5 py-0.5 bg-amber-100 hover:bg-amber-200 text-amber-800">
              <RotateCcw className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
      <div className="text-[10px] text-slate-500 mt-1 font-mono">
        {v.created_at?.slice(0, 16).replace('T', ' ')} · {v.user_name || v.user_id?.slice(0, 8)}
      </div>
      {v.reason && (
        <div className="text-[11px] text-slate-700 mt-1 italic">“{v.reason}”</div>
      )}
    </div>
  );
}


function DiffViewer({ diff }) {
  if (!diff) {
    return (
      <div className="text-center text-slate-400 text-xs py-6">
        اِختَر إصدارَين (A و B) لِعَرض الفُروقات.
      </div>
    );
  }
  const changes = diff.changes || {};
  const keys = Object.keys(changes);
  if (keys.length === 0) {
    return (
      <div className="text-center text-emerald-700 text-xs py-6 flex items-center justify-center gap-1.5">
        <Equal className="w-4 h-4" /> لا فُروقات بَين هَذَين الإصدارَين (التِّلِيمتري لا يُحسَب).
      </div>
    );
  }
  return (
    <div className="space-y-1.5" data-testid="diff-viewer">
      <div className="flex items-center justify-between text-[10px] font-mono text-slate-500">
        <div>A · v{diff.from.version_no} ({diff.from.action})</div>
        <ChevronLeft className="w-3 h-3" />
        <div>B · v{diff.to.version_no} ({diff.to.action})</div>
      </div>
      {keys.map((k) => {
        const c = changes[k];
        return (
          <div key={k} className="rounded-lg border bg-white p-2 text-xs"
                data-testid={`diff-field-${k}`}>
            <div className="font-mono text-[11px] font-semibold text-slate-700 mb-1">{k}</div>
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-red-50 border border-red-200 rounded p-1.5 break-all">
                <div className="text-[9px] text-red-600 font-semibold flex items-center gap-1">
                  <Minus className="w-3 h-3" /> A
                </div>
                <div className="font-mono text-[11px] text-red-900">
                  {c.from === null || c.from === undefined ? <em className="text-slate-400">—</em> :
                    typeof c.from === 'boolean' ? String(c.from) :
                    typeof c.from === 'object' ? JSON.stringify(c.from) : String(c.from)}
                </div>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded p-1.5 break-all">
                <div className="text-[9px] text-emerald-700 font-semibold flex items-center gap-1">
                  <Plus className="w-3 h-3" /> B
                </div>
                <div className="font-mono text-[11px] text-emerald-900">
                  {c.to === null || c.to === undefined ? <em className="text-slate-400">—</em> :
                    typeof c.to === 'boolean' ? String(c.to) :
                    typeof c.to === 'object' ? JSON.stringify(c.to) : String(c.to)}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}


export default function RuleHistoryDrawer({ rule, onClose, onMutated }) {
  const [versions, setVersions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [a, setA] = useState(null);
  const [b, setB] = useState(null);
  const [diff, setDiff] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await axiosInstance.get(`/bank-rules/${rule.id}/versions`);
      setVersions(data.items || []);
      // Default selection: latest vs previous
      if ((data.items || []).length >= 2) {
        setB(data.items[0].version_no);
        setA(data.items[1].version_no);
      } else if ((data.items || []).length === 1) {
        setA(data.items[0].version_no);
        setB(data.items[0].version_no);
      }
    } catch { toast.error('فَشَل تَحميل الإصدارات'); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    if (rule?.id) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rule?.id]);

  useEffect(() => {
    if (a == null || b == null || a === b) { setDiff(null); return; }
    axiosInstance.get(`/bank-rules/${rule.id}/diff`,
      { params: { from_v: a, to_v: b } })
      .then((r) => setDiff(r.data))
      .catch(() => setDiff(null));
  }, [a, b, rule?.id]);

  const restore = async (version_no) => {
    const reason = window.prompt('سَبَب الاستِعادَة (اختياري):', '');
    if (reason === null) return;
    try {
      await axiosInstance.post(`/bank-rules/${rule.id}/restore`,
        { version_no, reason });
      toast.success(`تَم استِرجاع الإصدار v${version_no}`);
      load();
      onMutated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فَشَل الاستِرجاع');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="rule-history-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-4xl bg-white shadow-2xl h-full flex flex-col" dir="rtl">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50">
          <div className="flex items-center gap-2">
            <History className="w-5 h-5 text-indigo-600" />
            <div>
              <div className="text-base font-bold">تاريخ القاعِدَة</div>
              <div className="text-xs text-slate-500 font-mono">{rule?.name}</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="history-close"
                  className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-4 h-4" />
          </button>
        </div>

        {busy ? (
          <div className="flex-1 flex items-center justify-center text-sm text-slate-400">…تَحميل</div>
        ) : (
          <div className="flex-1 grid grid-cols-1 md:grid-cols-[280px,1fr] overflow-hidden">
            {/* Version list */}
            <div className="border-l overflow-y-auto p-3 space-y-2 bg-slate-50/50">
              <div className="text-[11px] uppercase text-slate-500 font-semibold mb-1">
                {versions.length} إصدار
              </div>
              {versions.map((v) => (
                <VersionRow key={v.version_no}
                            v={v}
                            isSelectedA={a === v.version_no}
                            isSelectedB={b === v.version_no}
                            onClick={() => { /* row click toggles A */
                              setA(v.version_no);
                            }}
                            onCompareA={(n) => setA(n)}
                            onCompareB={(n) => setB(n)}
                            onRestore={restore} />
              ))}
            </div>

            {/* Diff viewer */}
            <div className="overflow-y-auto p-4">
              <div className="rounded-xl border bg-slate-50 p-3 mb-3 flex items-center gap-2 text-xs">
                <GitCompareArrows className="w-4 h-4 text-indigo-600" />
                <span className="text-slate-700 font-semibold">مُقارَنَة A → B</span>
                <span className="font-mono text-[11px] text-slate-500">
                  {a != null ? `v${a}` : '—'}
                  <ChevronLeft className="inline w-3 h-3 mx-1" />
                  {b != null ? `v${b}` : '—'}
                </span>
              </div>
              <DiffViewer diff={diff} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
